import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import MPHButton from "../MPHButtons/MPHButton";
import { useApiAxiosRequests } from "src/utils/ApiService/MPHApis";
import { errorToast, successToast } from "src/utils/toast";
import "./MPHSweepstakes.scss";
import sweepstakesImage from "../../../assets/banners/banner.png";
import useAuth from "src/context/AuthContext";
import { useStorage } from "src/utils/storage";
import { BRAND_NAME, INTERNAL_REDIRECTION_URLS } from "src/config/constants";

const MPHSweepstakes = ({
  isLogIn,
  isLogin,
  acquisitionChannel,
  reward,
  externalId,
  onRequireLogin,
}: any) => {
  const navigate = useNavigate();
  const { redeemRewards, getRewards } = useApiAxiosRequests();
  const { tokens, user, clearStorage, setUserProfile, setOptIn } = useAuth();
  const storage = useStorage();
  const sweepstakesRewardId =
    process.env.REACT_APP_SWEEPSTAKES_REWARD_ID?.toString().trim();
  // const sweepstakesRewardId =
  // (process.env.REACT_APP_SWEEPSTAKES_REWARD_ID || "494298")?.toString().trim(); // for testing
  const [isLoading, setIsLoading] = useState(false);
  const [rewardName, setRewardName] = useState("");
  const [rewardDescription, setRewardDescription] = useState("");
  const [rewardEntryDate, setRewardEntryDate] = useState("");
  const [rewardExitDate, setRewardExitDate] = useState("");
  const [rewardImageUrl, setRewardImageUrl] = useState<string | null>(null);
  const [promotionCount, setPromotionCount] = useState(1);
  const [promotionSliderInput, setPromotionSliderInput] = useState(1);
  const redirectTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const logoutRedirectRef = useRef(false);
  const getRewardsRef = useRef(getRewards);
  const lastFetchKeyRef = useRef<string | null>(null);
  const [trueRewardId, setTrueRewardId] = useState(
    (reward ?? "").toString().trim() === sweepstakesRewardId
  );

  const dateFormatter = (date: string) => {
    //Enter: December 5, 2025 - January 31, 2026
    return new Date(date).toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  }

  const parseRewardDescription = useCallback((description: string) => {
    const trimmed = (description || "").trim();

    if (!trimmed) {
      return { header: "", highlights: [] as string[] };
    }

    const markerRegex = /(🏆|✓|🗓️|•)/;
    const firstMarkerIndex = trimmed.search(markerRegex);

    if (firstMarkerIndex === -1) {
      return { header: trimmed, highlights: [] as string[] };
    }

    const header = trimmed.slice(0, firstMarkerIndex).trim();
    const bulletSection = trimmed.slice(firstMarkerIndex);
    const highlights = bulletSection
      .split(/(?=🏆|✓|🗓️|•)/g)
      .map((part) => part.trim())
      .filter(Boolean);

    return { header, highlights };
  }, []);

  const renderHeadingAndCopy = () => {
    const { header, highlights } = parseRewardDescription(rewardDescription);
    const fallbackHighlights = [
      "🏆 Get in the fast lane to win!",
      "✓ Win NASCAR Race tickets!",
      "✓ Join or Opt In to Stay Rewarded Program",
      "🗓️ No Purchase Necessary",
      "Enter: December 5, 2025 - January 31, 2026",
    ];

    return (
      <>
        <h4 style={{ textAlign: "left", marginTop: "20px" }}>
          {rewardName || "MyPlace NASCAR Sweepstakes"}
        </h4>
        <div className="mph-sweepstakes-content">
          <p className="mph-sweepstakes-description">
            {header || "We’re getting the latest details for this reward."}
          </p>
          <div className="mph-sweepstakes-highlight">
            {(highlights.length ? highlights : fallbackHighlights).map(
              (item, index) => (
                <p key={index}>{item}</p>
              )
            )}
          </div>
          {highlights.length && <div className="mph-sweepstakes-highlight">
            <p>Enter: {rewardEntryDate ? dateFormatter(rewardEntryDate) : "-"} - {rewardExitDate ? dateFormatter(rewardExitDate) : "-"}</p>
          </div>}
          <br />
          <div className="mph-sweepstakes-highlight">
            <p>Winner Drawn: January 31, 2026</p>
          </div>
        </div>
      </>
    );
  };

  const renderSubmitButton = () => (
    <div className="mph-sweepstakes-button">
      <MPHButton
        disabled={isLoading}
        isLoading={isLoading}
        buttonText={"Optin + Redeem"}
        onbuttonClick={handleSubmit}
      />
    </div>
  );

  const renderTermsLink = (customStyle?: React.CSSProperties) => (
    <span
      className="mph-sweepstakes-terms"
      style={customStyle}
      onClick={() => {
        navigateToPage(INTERNAL_REDIRECTION_URLS.MPH_STAYREWARED_TERMS);
      }}
    >
      Terms and conditions apply
    </span>
  );

  const navigateToPage = (redirect?: any) => {
    window.open(redirect, "_blank");
  };

  const logoutAndRedirect = useCallback(
    (message?: string) => {
      if (logoutRedirectRef.current) {
        return;
      }

      logoutRedirectRef.current = true;
      if (onRequireLogin) {
        onRequireLogin();
      }
      setTrueRewardId(false);

      if (message) {
        errorToast(message);
      }

      clearStorage();
      setUserProfile(null);
      setOptIn(false);
      navigate("/login", { replace: true });
    },
    [clearStorage, navigate, onRequireLogin, setOptIn, setUserProfile]
  );

  const scheduleRedirect = () => {
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
    }
    redirectTimeoutRef.current = setTimeout(() => {
      navigate("/find-hotels");
    }, 5000);
  };

  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    getRewardsRef.current = getRewards;
  }, [getRewards]);

  useEffect(() => {
    const rewardIdentifier = (reward ?? "").toString().trim();

    if (!rewardIdentifier || rewardIdentifier !== sweepstakesRewardId) {
      lastFetchKeyRef.current = null;
      logoutAndRedirect("This reward is not available. Please try again later.");
      return;
    }

    setTrueRewardId(true);

    if (!user?.email || !tokens?.token) {
      lastFetchKeyRef.current = null;
      return;
    }

    const fetchKey = `${user.email}-${rewardIdentifier}-${tokens.token}`;

    if (lastFetchKeyRef.current === fetchKey) {
      return;
    }

    lastFetchKeyRef.current = fetchKey;

    let isMounted = true;

    const fetchRewards = async () => {
      try {
        setIsLoading(true);
        const response: any = await getRewardsRef.current(
          user.email,
          tokens.token,
          rewardIdentifier
        );
        const status = response?.data?.status || response?.status;
        const isSuccess =
          status?.success === true ||
          status?.success === "true" ||
          status?.code === "6001";

        if (!isSuccess) {
          const message =
            status?.message || "Unable to fetch reward details. Please try again later.";
          throw new Error(message);
        }

        const rewardData = response?.data?.reward || {};
        const languageSpecificInfo = rewardData?.languageSpecificInfo || [];
        const localizedReward =
          languageSpecificInfo.find((info: any) => info?.enabled) || languageSpecificInfo[0] || {};

        if (!isMounted) {
          return;
        }

        setRewardName(localizedReward?.name || rewardData?.label || "");
        setRewardDescription(localizedReward?.description || "");
        setRewardImageUrl(
          localizedReward?.imageUrl || localizedReward?.thumbnailUrl || null
        );
        setRewardEntryDate((rewardData?.startDateTime).split("T")[0] || "");
        setRewardExitDate((rewardData?.endDateTime).split("T")[0] || "");
      } catch (error: any) {
        if (isMounted) {
          const apiMessage =
            error?.response?.data?.status?.message ||
            error?.response?.data?.message ||
            error?.message ||
            "Unable to fetch reward details. Please try again later.";
          errorToast(apiMessage);
          console.error("Error during fetching rewards information:", apiMessage);
          lastFetchKeyRef.current = null;
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
        }
      }
    };

    fetchRewards();

    return () => {
      isMounted = false;
    };
  }, [logoutAndRedirect, reward, sweepstakesRewardId, tokens?.token, user?.email]);

  useEffect(() => {
    //set the promotion count post calculation from available points
    const costPerPromotion = 1000;
    const availablePoints = user?.loyalty_points || 0; //get the users available points
    setPromotionCount(Math.floor(availablePoints / costPerPromotion)); //set the promotion count based on the available points
  }, [user?.loyalty_points])

  const handlePromotionCountChange = (e: any, updateType: string) => {
    if (e) {
      const value = e.target.value;
      setPromotionSliderInput(Number(value));
    } else {
      if (updateType === "increment" && promotionSliderInput < promotionCount) {
        setPromotionSliderInput(promotionSliderInput + 1);
      } else if (updateType === "decrement" && promotionSliderInput > 1) {
        setPromotionSliderInput(promotionSliderInput - 1);
      }
    }
  }

  const handleSubmit = async () => {
    setIsLoading(true);
    try {
      let profileEmail = user?.email || "";
      if (!profileEmail) {
        const userProfile = (await storage.get("user_profile")) || "";
        if (userProfile) {
          const parsed = JSON.parse(userProfile);
          profileEmail = parsed?.email || "";
        }
      }

      profileEmail = profileEmail.toString().trim().toLowerCase();

      let authorizationToken = tokens?.token || "";
      if (!authorizationToken) {
        const rawTokens = (await storage.get("app_tokens")) || "";
        if (rawTokens) {
          const parsedTokens = JSON.parse(rawTokens);
          authorizationToken = parsedTokens?.token || "";
        }
      }

      if (!profileEmail || !authorizationToken) {
        errorToast("We couldn't validate your session. Please log in and try again.");
        return;
      }

      const rewardCode = (reward ?? "").toString().trim();
      // const memberExternalId = (externalId ?? "").toString().trim();

      if (!rewardCode) {
        errorToast("Reward code is required.");
        return;
      }

      const numericRewardId = /^\d+$/.test(rewardCode) ? Number(rewardCode) : rewardCode;

      const payload: any = {
        email: profileEmail,
        brand: BRAND_NAME,
        eventDateTime: new Date().toISOString(),
        rewards: [
          {
            rewardId: numericRewardId,
            quantity: promotionSliderInput,
          },
        ],
      };

      // if (memberExternalId) {
      //   payload.customer = {
      //     identifierType: "EXTERNAL_ID",
      //     identifierValue: memberExternalId,
      //   };
      // }

      const response: any = await redeemRewards(profileEmail, payload, authorizationToken);
      const status = response?.data?.status || response?.data?.response?.status;
      const isSuccess =
        (status && (status.success === true || status.success === "true")) ||
        status?.code === "1100" ||
        (response?.status === 200 && (response?.data?.status?.success === true || response?.data?.status?.success === "true"));

      if (isSuccess) {
        successToast("Enrolled For Sweepstakes Successfully!");
        scheduleRedirect();
      } else {
        const errorMessage =
          status?.message ||
          response?.data?.message ||
          "Redemption Failed. Please Try Again Later.";
        errorToast(errorMessage);
        scheduleRedirect();
      }
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.status?.message ||
        error?.response?.data?.message ||
        error?.message;
      errorToast("An error occurred while processing your request. Please try again later.");
      console.error("Error during Redemption:", apiMessage ? apiMessage : error);
      scheduleRedirect();
    } finally {
      setIsLoading(false);
    }
  };

  if (!trueRewardId) {
    return null;
  }

  return (
    <>
      <div className="mph-sweepstakes-container">
        <div className="mph-sweepstakes-layout">
          <div className="mph-sweepstakes-left">{renderHeadingAndCopy()}</div>
          <div className="mph-sweepstakes-right">
            <img
              src={rewardImageUrl || sweepstakesImage}
              alt={rewardName || "MyPlace Sweepstakes Reward"}
            />
          </div>
        </div>
        <div className="mph-sweepstakes-slider-container">
          <div className="mph-sweepstakes-slider">
            <button className="mph-sweepstakes-slider-decr btn btn-light" onClick={() => handlePromotionCountChange(null, "decrement")} disabled={promotionSliderInput <= 1}> - </button>
            <input type="range" name="" className="sweepstakes-slider-range-input" min="1" max={promotionCount} onChange={(e) => handlePromotionCountChange(e, "null")} value={promotionSliderInput} />
            <button className="mph-sweepstakes-slider-incr btn btn-light" onClick={() => handlePromotionCountChange(null, "increment")} disabled={promotionSliderInput >= promotionCount}> + </button>
            <input
              type="text"
              name=""
              id="promotionCount"
              value={promotionSliderInput}
              readOnly
              className="mph-sweepstakes-slider-disp form-control text-center"
            />
          </div>
        </div>
        <div className="mph-sweepstakes-controls">{renderSubmitButton()}</div>
        {renderTermsLink({ marginTop: "20px" })}
      </div>
    </>
  );
};

export default MPHSweepstakes;

